import { useLocalParticipant } from "@livekit/components-react"
import LiveEventBuffer from "@data/services/LiveEventBuffer"
import { Track } from "livekit-client"
import { useCallback, useEffect, useRef, useState } from "react"
import {
    CANVAS_HEIGHT,
    CANVAS_WIDTH,
    CONSECUTIVE_SAMPLES_TO_CONFIRM,
    isFrameObstructed,
    luminanceStats,
} from "../../../engine/precall/cameraFrameAnalysis"

// Thresholds and the luminance measurement itself now live in
// `cameraFrameAnalysis`, shared with the pre-call device check so both judge a
// frame by the same rule. Only the cadence stays here: background monitoring
// can take its time, the entry check cannot.
// Goal: ~10 s to detect, 0 false positive on normally-lit setup.
const SAMPLE_INTERVAL_MS = 3000

/**
 * Detects when the local participant's camera lens is covered (uniform dark
 * frame) and exposes UI state + emits cam_obstructed_start/end events.
 *
 * Web only — relies on Canvas getImageData, runs only when caller passes
 * enabled=true (typically: IS_WEB && user is tutor).
 *
 * Self-guards: bails out cleanly when the camera is disabled or the track
 * isn't available; emits cam_obstructed_end when transitioning out of
 * obstructed state, including when the cause is "camera disabled".
 *
 * Leaving the live is covered by the explicit closeOutObstructedState() calls
 * in LiveModeFooter, before disconnect. The unmount cleanup below is NOT a
 * safety net: the owning screen destroys the shared buffer on its own unmount,
 * and once destroyed the buffer drops any late record(). A start can therefore
 * stay unpaired (tab closed, crash, network loss) — see the note on the pair
 * in LiveSessionLogLabel::MAP: consumers must bound it on the end of session.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Master switch (compose with role + IS_WEB upstream)
 * @returns {{ isObstructed: boolean, popoverVisible: boolean, dismissPopover: () => void, closeOutObstructedState: () => void }}
 */
export function useTutorCameraObstructionDetection({ enabled }) {
    const { isCameraEnabled, localParticipant } = useLocalParticipant()

    const [isObstructed, setIsObstructed] = useState(false)
    const [popoverVisible, setPopoverVisible] = useState(false)

    // Refs to keep stable identity inside the sampling interval closure.
    // isObstructedRef is written synchronously by both writers of isObstructed
    // (closeOutObstructedState and sampleFrame) — deliberately, so an unmount
    // landing before the next React commit can't read a stale value. No effect
    // mirrors it: that would only ever write back what the ref already holds.
    const isObstructedRef = useRef(false)
    const popoverDismissedRef = useRef(false)
    const obstructedSamplesRef = useRef(0)
    const clearSamplesRef = useRef(0)

    // Long-lived DOM nodes + analysis buffers — allocated once, reused per sample.
    const videoRef = useRef(null)
    const canvasRef = useRef(null)
    const ctxRef = useRef(null)
    const lumasRef = useRef(null)

    // Reset state to "not obstructed" and emit cam_obstructed_end if we were
    // mid-obstruction. The ref is updated synchronously so a unmount happening
    // before the next React commit doesn't read a stale `true` and re-emit.
    const closeOutObstructedState = useCallback(() => {
        if (isObstructedRef.current) {
            LiveEventBuffer.getInstance().record("cam_obstructed_end", {})
            isObstructedRef.current = false
            setIsObstructed(false)
        }
        setPopoverVisible(false)
        popoverDismissedRef.current = false
        obstructedSamplesRef.current = 0
        clearSamplesRef.current = 0
    }, [])

    const sampleFrame = useCallback(() => {
        const video = videoRef.current
        const ctx = ctxRef.current
        const lumas = lumasRef.current
        if (!video || !ctx || !lumas) return

        // Resolve the publication on every sample. LiveKit can replace the
        // underlying MediaStreamTrack during reconnects or device switches
        // without toggling isCameraEnabled or replacing localParticipant.
        const publication = localParticipant?.getTrackPublication(
            Track.Source.Camera
        )
        const mediaStreamTrack = publication?.track?.mediaStreamTrack
        const attachedTrack = video.srcObject?.getVideoTracks?.()[0]

        if (!mediaStreamTrack || mediaStreamTrack.readyState === "ended") {
            if (video.srcObject) video.srcObject = null
            closeOutObstructedState()
            return
        }

        if (attachedTrack !== mediaStreamTrack) {
            video.srcObject = new MediaStream([mediaStreamTrack])
            obstructedSamplesRef.current = 0
            clearSamplesRef.current = 0
            video.play().catch(() => {
                // A later sample retries once the track can play.
            })
            return
        }

        if (video.readyState < 2) {
            video.play().catch(() => {})
            return
        }

        try {
            ctx.drawImage(video, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)
        } catch {
            return
        }
        let imageData
        try {
            imageData = ctx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)
        } catch {
            return
        }
        const obstructedFrame = isFrameObstructed(luminanceStats(imageData.data, lumas))

        if (obstructedFrame) {
            obstructedSamplesRef.current += 1
            clearSamplesRef.current = 0
            if (
                !isObstructedRef.current &&
                obstructedSamplesRef.current >=
                    CONSECUTIVE_SAMPLES_TO_CONFIRM
            ) {
                isObstructedRef.current = true
                setIsObstructed(true)
                if (!popoverDismissedRef.current) {
                    setPopoverVisible(true)
                }
                LiveEventBuffer.getInstance().record(
                    "cam_obstructed_start",
                    {}
                )
            }
        } else {
            clearSamplesRef.current += 1
            obstructedSamplesRef.current = 0
            if (
                isObstructedRef.current &&
                clearSamplesRef.current >= CONSECUTIVE_SAMPLES_TO_CONFIRM
            ) {
                closeOutObstructedState()
            }
        }
    }, [closeOutObstructedState, localParticipant])

    useEffect(() => {
        if (!enabled || !isCameraEnabled) {
            // Camera off (or hook disabled) → if we were obstructed, close out cleanly.
            closeOutObstructedState()
            return
        }

        // Lazily build the off-screen video + canvas, reused across re-runs of
        // this effect (camera toggled, track replaced). NOT across mounts: the
        // unmount cleanup below removes the <video> from the DOM and nulls the
        // refs, so a remount allocates a fresh pair — no orphan node, and the
        // interval is cleared by this effect's own cleanup.
        if (!videoRef.current) {
            const v = document.createElement("video")
            v.muted = true
            v.autoplay = true
            v.playsInline = true
            v.style.position = "fixed"
            v.style.top = "-9999px"
            v.style.left = "-9999px"
            v.style.width = "1px"
            v.style.height = "1px"
            v.style.pointerEvents = "none"
            document.body.appendChild(v)
            videoRef.current = v
        }
        if (!canvasRef.current) {
            const c = document.createElement("canvas")
            c.width = CANVAS_WIDTH
            c.height = CANVAS_HEIGHT
            canvasRef.current = c
            ctxRef.current = c.getContext("2d", { willReadFrequently: true })
            lumasRef.current = new Float32Array(CANVAS_WIDTH * CANVAS_HEIGHT)
        }

        sampleFrame()
        const intervalId = setInterval(sampleFrame, SAMPLE_INTERVAL_MS)
        return () => {
            clearInterval(intervalId)
            if (videoRef.current) videoRef.current.srcObject = null
        }
    }, [
        closeOutObstructedState,
        enabled,
        isCameraEnabled,
        sampleFrame,
    ])

    // Final cleanup on unmount — emit cam_obstructed_end if still obstructed,
    // and remove the hidden DOM nodes.
    useEffect(() => {
        return () => {
            if (isObstructedRef.current) {
                LiveEventBuffer.getInstance().record(
                    "cam_obstructed_end",
                    {}
                )
            }
            if (videoRef.current) {
                videoRef.current.srcObject = null
                if (videoRef.current.parentNode) {
                    videoRef.current.parentNode.removeChild(videoRef.current)
                }
                videoRef.current = null
            }
            canvasRef.current = null
            ctxRef.current = null
            lumasRef.current = null
        }
    }, [])

    const dismissPopover = () => {
        setPopoverVisible(false)
        popoverDismissedRef.current = true
    }

    return {
        isObstructed,
        popoverVisible,
        dismissPopover,
        closeOutObstructedState,
    }
}
